import { useSettingsStore } from "@/stores/settings";
import moment from "moment";

let useDefaultFormatting = () => ({
  date: {
    datetime: "DD.MM.YYYY HH:mm",
    date: "DD.MM.YYYY",
    time: "HH:mm",
  },
});

export function strippedHtml(html: string, maxLength?: number) {
  if (!html) {
    return "";
  }

  html = String(html);

  html = html
    .replace(/^b'|\\n'$/g, "")
    .replace(/\\n|\n/g, "")
    .replace(/<\/(\w+)>/g, "</$1> ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return truncateString(html, maxLength);
}

export function truncateString(str: string | null, maxLength?: number) {
  if (str && maxLength && str.length >= maxLength) {
    return `${str.slice(0, maxLength)}...`;
  }
  return str || "";
}

export function toCamelCase(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (match, chr) => chr.toUpperCase());
}

export function formatDate(date: string | Date | null | undefined, format?: string, addHtml?: boolean) {
  const { defaultFormatting } = useSettingsStore();

  format = format || defaultFormatting.date.datetime;
  if (!date) {
    return "";
  }

  const dateObj = new Date(date);
  if (dateObj.getMinutes() === 0 && dateObj.getHours() === 0) {
    return moment(date).format(defaultFormatting.date.date);
  }

  if (addHtml) {
    return `<span class="flex -mb-7 text-3xl">${moment(date).format(defaultFormatting.date.date)}</span><br><span class="text-caption">${moment(date).format(defaultFormatting.date.time)}</span>`;
  } else {
    return moment(date).format(format);
  }
}

export function formatDateRange(from: string | Date | null, to: string | Date | null, format?: string, addHtml?: boolean) {
  const { defaultFormatting } = useSettingsStore();
  format = format || defaultFormatting.date.datetime;

  if (!from && !to) {
    return "-";
  }

  if (!from) {
    return `do ${formatDate(to, format)}`;
  }

  if (!to) {
    return `od ${formatDate(from, format)}`;
  }

  if (from === to) {
    return formatDate(from, format);
  }

  if (addHtml) {
    return `<span class="text-caption">od</span> ${formatDate(from, format)}<br><span class="text-caption">do</span> ${formatDate(to, format)}`;
  } else {
    return `od ${formatDate(from, format)} do ${formatDate(to, format)}`;
  }
}

export function getDate(date?: string | Date | null) {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    return date;
  }

  return new Date(date);
}

export function formatPrice(price: any, currency: string = "CZK") {
  if (typeof price !== "number") {
    price = parseFloat(price || "");
  }

  if (isNaN(price)) {
    price = 0;
  }

  const formattedPrice = price
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const currencySymbolMap: { [key: string]: string } = {
    CZK: "Kč",
    EUR: "€",
    USD: "$",
  };

  return `${formattedPrice} ${currencySymbolMap[currency] || (currency ?? "")}`;
}

export function formatNumberOfMonths(count: number | null) {
  count = count || 0;
  return `date.monthInflect.${count === 1 ? "one" : count === 0 || count > 4 ? "many" : "few"}`;
}

export function formatFileSize(bytes: number) {
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const roundingFactor = i < 2 ? 0 : 2;
  return bytes ? (bytes / Math.pow(1024, i)).toFixed(roundingFactor) + " " + sizes[i] : "0 B";
}

export function webalize(str: string): string {
  const charlist: [string, string][] = [
    ["Á", "A"],
    ["Ä", "A"],
    ["Č", "C"],
    ["Ç", "C"],
    ["Ď", "D"],
    ["É", "E"],
    ["Ě", "E"],
    ["Ë", "E"],
    ["Í", "I"],
    ["Ň", "N"],
    ["Ó", "O"],
    ["Ö", "O"],
    ["Ř", "R"],
    ["Š", "S"],
    ["Ť", "T"],
    ["Ú", "U"],
    ["Ů", "U"],
    ["Ü", "U"],
    ["Ý", "Y"],
    ["Ž", "Z"],
    ["á", "a"],
    ["ä", "a"],
    ["č", "c"],
    ["ç", "c"],
    ["ď", "d"],
    ["é", "e"],
    ["ě", "e"],
    ["ë", "e"],
    ["í", "i"],
    ["ň", "n"],
    ["ó", "o"],
    ["ö", "o"],
    ["ř", "r"],
    ["š", "s"],
    ["ť", "t"],
    ["ú", "u"],
    ["ů", "u"],
    ["ü", "u"],
    ["ý", "y"],
    ["ž", "z"],
  ];

  for (const [original, replacement] of charlist) {
    const re = new RegExp(original, "g");
    str = str.replace(re, replacement);
  }

  str = str.replace(/[^a-z0-9]/gi, "-");
  str = str.replace(/\-+/g, "-");

  if (str[0] === "-") {
    str = str.substring(1);
  }

  if (str[str.length - 1] === "-") {
    str = str.substring(0, str.length - 1);
  }

  return str.toLowerCase();
}

export function extractFileName(filePath: File | string | null): string {
  if (filePath instanceof File) {
    return filePath.name;
  }

  if (!filePath || filePath.trim() === "") {
    return "";
  }

  const match = filePath.match(/([^\/\\]+)$/);

  if (!match || match.length < 2) {
    return "";
  }

  return match[1];
}

export function getFileMimeIcon(filePath: File | string | null): string {
  if (filePath instanceof File) {
    return getFileMimeIcon(filePath.name);
  }

  if (!filePath?.trim()) return "mdi mdi-file-outline";

  const extension = filePath.split(".").pop()?.toLowerCase();
  if (!extension) return "mdi mdi-file-outline";

  const mimeIconMap: { [key: string]: string[] } = {
    "mdi mdi-file-image-outline": ["png", "jpg", "jpeg", "gif", "svg", "bmp", "webp"],
    "mdi mdi-file-word-outline": ["doc", "docx"],
    "mdi mdi-file-excel-outline": ["xls", "xlsx"],
    "mdi mdi-file-powerpoint-outline": ["ppt", "pptx"],
    "mdi mdi-file-document-outline": ["pdf", "txt", "md"],
    "mdi mdi-folder-zip-outline": ["zip", "rar", "7z", "tar", "gz"],
    "mdi mdi-file-music-outline": ["mp3", "wav", "flac"],
    "mdi mdi-file-video-outline": ["mp4", "mkv", "avi", "mov"],
    "mdi mdi-file-code-outline": ["js", "ts", "html", "css", "json", "xml", "py", "java", "c", "cpp", "cs"],
  };

  return Object.entries(mimeIconMap).find(([_, exts]) => exts.includes(extension))?.[0] || "mdi mdi-file-outline";
}
